// Phase 58 — Manager Cash Ledger.
//
// Per-manager (per-user) view of the physical cash THIS manager is holding.
// Mirrors the visual structure of StaffLedger but the data model is simpler:
//   * Every entry is a "cash intake" — manager taking money in.
//   * `cash_type` discriminates "simple" (off-books) from "invoice" (on-books).
//   * Source = "manual" (ad-hoc entry) or "booking" (auto from approval).
//
// Endpoints:
//   GET  /manager-ledger                  → list
//   GET  /manager-ledger/summary          → totals
//   POST /manager-ledger/entries          → ad-hoc cash entry
//   POST /manager-ledger/settle           → settle to zero
//   GET  /manager-ledger/settlements      → past settlements
//   GET  /manager-ledger/settlements/:id/receipt.pdf → PDF receipt
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Minus, Wallet, FileText, Coins, CheckCircle2, Loader2,
  Printer, Receipt, Search, X, MoreHorizontal, Pencil, Trash2, History as HistoryIcon,
  AlertTriangle,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import StatusHistory from "@/components/StatusHistory";
import LedgerFullHistoryDialog from "@/components/LedgerFullHistoryDialog";

const fmtMoney = (n, ccy = "EUR") => {
  const num = Number(n || 0);
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency", currency: ccy, currencyDisplay: "narrowSymbol",
    }).format(num);
  } catch {
    return `€${num.toFixed(2)}`;
  }
};

const fmtDateTime = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const todayInput = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

async function downloadManagerSettlement(settlementId) {
  if (!settlementId) return;
  const token = localStorage.getItem("jb_token");
  try {
    const res = await fetch(
      `${API_BASE}/manager-ledger/settlements/${settlementId}/receipt.pdf`,
      { headers: token ? { Authorization: `Bearer ${token}` } : {} },
    );
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `manager-settlement-${settlementId.split("-")[0]}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch (e) {
    toast.error(`Receipt download failed: ${e?.message || e}`);
  }
}

function CustomerCombo({ value, onChange }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  // Resolve the label for an externally-set id (e.g. on edit).
  useEffect(() => {
    if (!value) { setSelected(null); return; }
    if (selected?.id === value) return;
    api.get(`/customers/${value}`).then((r) => setSelected(r.data)).catch(() => setSelected(null));
  }, [value, selected?.id]);

  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/customers", { params: { q: q.trim() } });
        setResults(Array.isArray(r.data) ? r.data.slice(0, 8) : []);
      } catch { setResults([]); }
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className="w-full justify-start gap-2 h-10 font-normal"
          data-testid="mgr-ledger-customer-trigger"
        >
          <Search className="h-4 w-4 text-muted-foreground" />
          {selected ? (
            <span className="truncate">{selected.company_name || selected.contact_name}</span>
          ) : (
            <span className="text-muted-foreground">Search customer (optional)…</span>
          )}
          {selected && (
            <span
              className="ml-auto p-1 rounded hover:bg-muted"
              onClick={(e) => { e.stopPropagation(); onChange(null); setSelected(null); }}
              role="button"
              tabIndex={0}
              data-testid="mgr-ledger-customer-clear"
            >
              <X className="h-3.5 w-3.5" />
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80" align="start">
        <Input
          autoFocus
          placeholder="Type name or phone…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="h-9 mb-2"
          data-testid="mgr-ledger-customer-search"
        />
        <div className="max-h-64 overflow-y-auto divide-y divide-border">
          {results.length === 0 ? (
            <div className="text-xs text-muted-foreground p-2">
              {q.trim() ? "No matches." : "Start typing to search."}
            </div>
          ) : results.map((c) => (
            <button
              key={c.id}
              type="button"
              className="w-full text-left p-2 hover:bg-secondary/60 rounded"
              onClick={() => { onChange(c.id); setSelected(c); setOpen(false); setQ(""); }}
              data-testid={`mgr-ledger-customer-option-${c.id}`}
            >
              <div className="text-sm font-bold">{c.company_name}</div>
              {c.contact_name && (
                <div className="text-xs text-muted-foreground">{c.contact_name}</div>
              )}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function AddEntryDialog({ open, onOpenChange, onCreated, mode = "intake" }) {
  const isExpense = mode === "expense";
  const [amount, setAmount] = useState("");
  const [entryDate, setEntryDate] = useState(todayInput());
  const [cashType, setCashType] = useState("invoice");
  const [customerId, setCustomerId] = useState(null);
  const [notes, setNotes] = useState("");
  const [category, setCategory] = useState("Fuel");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setAmount("");
    setEntryDate(todayInput());
    setCashType("invoice");
    setCustomerId(null);
    setNotes("");
    setCategory("Fuel");
    setBusy(false);
  }, [open]);

  const submit = async () => {
    const amt = parseFloat(amount);
    if (!Number.isFinite(amt) || amt <= 0) {
      toast.error("Amount must be greater than 0");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        amount: amt,
        entry_date: entryDate,
        cash_type: cashType,
        customer_id: customerId || null,
        notes,
        kind: isExpense ? "expense" : "intake",
      };
      if (isExpense) payload.category = category;
      const r = await api.post("/manager-ledger/entries", payload);
      toast.success(`${isExpense ? "Expense" : "Cash entry"} of ${fmtMoney(amt)} added`);
      onCreated?.(r.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid={isExpense ? "mgr-ledger-add-expense-dialog" : "mgr-ledger-add-dialog"}>
        <DialogHeader>
          <DialogTitle>{isExpense ? "Add expense" : "Add cash entry"}</DialogTitle>
          <DialogDescription>
            {isExpense
              ? "Record cash you spent on company expenses. Subtracts from your held balance."
              : "Record physical cash you collected outside the booking flow. This is a ledger-only entry — no booking or invoice is created."}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <div>
            <Label className="mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
              Amount (€)
            </Label>
            <Input
              type="number"
              step="0.01"
              min="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              className="h-10 mt-1"
              data-testid={isExpense ? "mgr-ledger-expense-amount" : "mgr-ledger-amount"}
              autoFocus
            />
          </div>
          <div>
            <Label className="mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
              Date
            </Label>
            <Input
              type="date"
              value={entryDate}
              onChange={(e) => setEntryDate(e.target.value)}
              className="h-10 mt-1"
              data-testid={isExpense ? "mgr-ledger-expense-date" : "mgr-ledger-date"}
            />
            <p className="text-[10px] text-muted-foreground mt-1">
              You can backdate up to 90 days.
            </p>
          </div>
          {isExpense && (
            <div>
              <Label className="mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                Category
              </Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="h-10 mt-1" data-testid="mgr-ledger-expense-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {["Fuel", "Tools", "Materials", "Refreshments", "Vehicle", "Other"].map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
              Cash type
            </Label>
            <div className="grid grid-cols-2 gap-2 mt-1" role="radiogroup">
              {[
                { code: "simple", title: "Simple Cash", sub: "Off-books" },
                { code: "invoice", title: "Cash with Invoice", sub: "On-books · VAT" },
              ].map((opt) => {
                const on = cashType === opt.code;
                return (
                  <button
                    key={opt.code}
                    type="button"
                    role="radio"
                    aria-checked={on}
                    onClick={() => setCashType(opt.code)}
                    className={`text-left rounded-[8px] border px-3 py-2 transition-colors ${
                      on
                        ? "border-accent bg-accent/10 ring-2 ring-accent/30"
                        : "border-border hover:border-accent/60"
                    }`}
                    data-testid={`mgr-ledger-${isExpense ? "expense-" : ""}cashtype-${opt.code}`}
                  >
                    <div className="text-sm font-bold">{opt.title}</div>
                    <div className="text-[10px] text-muted-foreground mt-0.5">{opt.sub}</div>
                  </button>
                );
              })}
            </div>
          </div>
          {!isExpense && (
            <div>
              <Label className="mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                Customer (optional)
              </Label>
              <div className="mt-1">
                <CustomerCombo value={customerId} onChange={setCustomerId} />
              </div>
            </div>
          )}
          <div>
            <Label className="mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
              Notes
            </Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={isExpense ? "Receipt #, vendor, etc. (optional)" : "Anything worth remembering (optional)…"}
              className="mt-1"
              rows={2}
              data-testid={isExpense ? "mgr-ledger-expense-notes" : "mgr-ledger-notes"}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}
                  data-testid={isExpense ? "mgr-ledger-expense-cancel" : "mgr-ledger-add-cancel"}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={busy}
                  className={isExpense ? "gap-2 bg-rose-600 hover:bg-rose-700 text-white" : "gap-2 btn-cta"}
                  data-testid={isExpense ? "mgr-ledger-expense-submit" : "mgr-ledger-add-submit"}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (isExpense ? <Minus className="h-4 w-4" /> : <Plus className="h-4 w-4" />)}
            {isExpense ? "Add expense" : "Add entry"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SettleDialog({ open, onOpenChange, totalHeld, onSettled }) {
  const [method, setMethod] = useState("bank_deposit");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setMethod("bank_deposit");
    setNotes("");
    setBusy(false);
  }, [open]);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/manager-ledger/settle", {
        method,
        // Phase 60.2 — signed amount (intake − expense). Backend compares abs values.
        amount: totalHeld,
        notes,
      });
      toast.success("Ledger settled to zero — receipt generated.");
      onSettled?.(r.data?.settlement);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="mgr-ledger-settle-dialog">
        <DialogHeader>
          <DialogTitle>Settle ledger to zero</DialogTitle>
          <DialogDescription>
            This stamps every outstanding entry as settled and produces a PDF
            receipt. You&apos;re confirming you&apos;ve handed over <strong>{fmtMoney(totalHeld)}</strong>.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <div>
            <Label className="mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
              Method
            </Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger className="h-10 mt-1" data-testid="mgr-ledger-settle-method">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="bank_deposit">Bank deposit</SelectItem>
                <SelectItem value="cash_handover">Cash handover</SelectItem>
                <SelectItem value="adjustment_writeoff">Adjustment write-off</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
              Notes
            </Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Deposit reference, bag count, etc. (optional)"
              rows={2}
              className="mt-1"
              data-testid="mgr-ledger-settle-notes"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}
                  data-testid="mgr-ledger-settle-cancel">
            Cancel
          </Button>
          <Button onClick={submit} disabled={busy || totalHeld <= 0}
                  className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="mgr-ledger-settle-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
            Settle {fmtMoney(totalHeld)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ManagerLedger() {
  const [summary, setSummary] = useState(null);
  const [entries, setEntries] = useState([]);
  const [settlements, setSettlements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    cash_type: "all",
    customer_id: null,
    from: "",
    to: "",
  });
  const [addOpen, setAddOpen] = useState(false);
  const [addExpenseOpen, setAddExpenseOpen] = useState(false);
  const [settleOpen, setSettleOpen] = useState(false);
  // Phase P.30 — full-ledger audit history modal.
  const [historyOpen, setHistoryOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.cash_type !== "all") params.cash_type = filters.cash_type;
      if (filters.customer_id) params.customer_id = filters.customer_id;
      if (filters.from) params.from = filters.from;
      if (filters.to) params.to = filters.to;
      const [sumR, listR, settleR] = await Promise.all([
        api.get("/manager-ledger/summary"),
        api.get("/manager-ledger", { params }),
        api.get("/manager-ledger/settlements"),
      ]);
      setSummary(sumR.data);
      setEntries(Array.isArray(listR.data) ? listR.data : []);
      setSettlements(settleR.data?.settlements || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [
    filters.cash_type, filters.customer_id, filters.from, filters.to,
  ]);

  const unsettledOnly = useMemo(
    () => entries.filter((e) => !e.settlement_id),
    [entries],
  );

  const totalHeld = summary?.total_held ?? 0;
  const totalSimple = summary?.total_simple ?? 0;
  const totalInvoice = summary?.total_invoice ?? 0;
  const canSettle = Math.abs(totalHeld) > 0.005;

  return (
    <div className="page" data-testid="manager-ledger-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="mono text-[10px] uppercase tracking-[0.3em] text-accent">// Manager</div>
          <h1 className="text-3xl md:text-4xl font-black tracking-tight mt-2">
            My Cash Ledger
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Track the physical cash you&apos;re holding. Only visible to you (and super-admin).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/">
            <Button variant="ghost" size="sm" className="gap-2" data-testid="mgr-ledger-back">
              <ArrowLeft className="h-4 w-4" /> Back
            </Button>
          </Link>
          <Button
            onClick={() => setAddExpenseOpen(true)}
            variant="outline"
            className="gap-2 border-rose-300 text-rose-700 hover:bg-rose-50"
            data-testid="mgr-ledger-add-expense-button"
          >
            <Minus className="h-4 w-4" /> Add expense
          </Button>
          <Button
            onClick={() => setAddOpen(true)}
            className="btn-cta gap-2"
            data-testid="mgr-ledger-add-button"
          >
            <Plus className="h-4 w-4" /> Add intake
          </Button>
          <Button
            onClick={() => setHistoryOpen(true)}
            variant="outline"
            className="gap-2"
            data-testid="mgr-ledger-full-history-btn"
          >
            <HistoryIcon className="h-4 w-4" /> Full history
          </Button>
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-6" data-testid="mgr-ledger-summary">
        <SummaryTile
          icon={<Wallet className={`h-5 w-5 ${totalHeld < 0 ? "text-rose-700" : "text-amber-700"}`} />}
          label="Currently holding"
          value={fmtMoney(totalHeld)}
          sub={`${summary?.unsettled_entry_count || 0} unsettled entries`}
          tone={totalHeld < 0 ? "negative" : "amber"}
          testid="mgr-ledger-summary-held"
        />
        <SummaryTile
          icon={<Coins className="h-5 w-5 text-violet-700" />}
          label="Simple Cash (off-books)"
          value={fmtMoney(totalSimple)}
          sub="Intake · not in accounts export"
          testid="mgr-ledger-summary-simple"
        />
        <SummaryTile
          icon={<FileText className="h-5 w-5 text-sky-700" />}
          label="Cash with Invoice (on-books)"
          value={fmtMoney(totalInvoice)}
          sub="Intake · in accounts export"
          testid="mgr-ledger-summary-invoice"
        />
        <SummaryTile
          icon={<Minus className="h-5 w-5 text-rose-700" />}
          label="Expenses (unsettled)"
          value={fmtMoney(summary?.expense_total || 0)}
          sub={`Simple ${fmtMoney(summary?.expense_simple || 0)} · Invoice ${fmtMoney(summary?.expense_invoice || 0)}`}
          tone="rose"
          testid="mgr-ledger-summary-expense"
        />
      </div>

      {/* Settle CTA */}
      {canSettle && (
        <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-[8px] p-4 flex items-center justify-between flex-wrap gap-3"
             data-testid="mgr-ledger-settle-cta">
          <div>
            <div className="text-sm font-bold text-emerald-900">
              Ready to settle? Hand over {fmtMoney(totalHeld)} and zero the ledger.
            </div>
            <div className="text-xs text-emerald-800/80 mt-0.5">
              We&apos;ll generate a PDF receipt with every entry rolled in.
            </div>
          </div>
          <Button
            onClick={() => setSettleOpen(true)}
            className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
            data-testid="mgr-ledger-settle-open"
          >
            <CheckCircle2 className="h-4 w-4" /> Settle to zero
          </Button>
        </div>
      )}

      {/* Filters */}
      <div className="mt-6 flex flex-wrap items-end gap-2" data-testid="mgr-ledger-filters">
        <div>
          <Label className="mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
            Cash type
          </Label>
          <Select
            value={filters.cash_type}
            onValueChange={(v) => setFilters((f) => ({ ...f, cash_type: v }))}
          >
            <SelectTrigger className="h-10 w-[180px] mt-1" data-testid="mgr-ledger-filter-cashtype">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All cash types</SelectItem>
              <SelectItem value="simple">Simple Cash only</SelectItem>
              <SelectItem value="invoice">With Invoice only</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
            From
          </Label>
          <Input type="date" value={filters.from}
                 onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))}
                 className="h-10 mt-1 w-[160px]" data-testid="mgr-ledger-filter-from" />
        </div>
        <div>
          <Label className="mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
            To
          </Label>
          <Input type="date" value={filters.to}
                 onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))}
                 className="h-10 mt-1 w-[160px]" data-testid="mgr-ledger-filter-to" />
        </div>
        <div className="min-w-[240px] flex-1">
          <Label className="mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
            Customer
          </Label>
          <div className="mt-1">
            <CustomerCombo
              value={filters.customer_id}
              onChange={(v) => setFilters((f) => ({ ...f, customer_id: v }))}
            />
          </div>
        </div>
        {(filters.cash_type !== "all" || filters.customer_id || filters.from || filters.to) && (
          <Button
            variant="ghost"
            size="sm"
            className="gap-1 mb-0.5"
            onClick={() => setFilters({ cash_type: "all", customer_id: null, from: "", to: "" })}
            data-testid="mgr-ledger-filter-clear"
          >
            <X className="h-3.5 w-3.5" /> Clear
          </Button>
        )}
      </div>

      {/* Entries list — Phase P.14 bank-statement style */}
      <div className="mt-6">
        <div className="mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-2">
          // Ledger entries · {entries.length}
        </div>
        {loading && entries.length === 0 ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : entries.length === 0 ? (
          <div className="bg-card border border-dashed border-border rounded-[8px] p-10 text-center"
               data-testid="mgr-ledger-empty">
            <Wallet className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
            <div className="text-sm font-bold">No cash entries yet.</div>
            <div className="text-xs text-muted-foreground mt-1">
              Use <span className="font-bold">Add cash entry</span> to record manually-collected cash.
            </div>
          </div>
        ) : (
          <LedgerStatementTable entries={entries} onChanged={load} />
        )}
      </div>

      {/* Settlement history */}
      {settlements.length > 0 && (
        <div className="mt-10">
          <div className="mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-2">
            // Settlement history · {settlements.length}
          </div>
          <div className="space-y-2" data-testid="mgr-ledger-settlements-list">
            {settlements.map((s) => (
              <div key={s.id}
                   className="bg-card border border-border rounded-[8px] p-3 flex items-center justify-between gap-3 flex-wrap"
                   data-testid={`mgr-ledger-settlement-${s.id}`}>
                <div>
                  <div className="text-sm font-bold">
                    {fmtMoney(s.amount_settled, s.currency)} · {(s.method || "").replace(/_/g, " ")}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {fmtDateTime(s.settled_at)}
                    {s.ledger_entry_ids?.length ? ` · ${s.ledger_entry_ids.length} entries rolled in` : ""}
                  </div>
                  {s.notes && (
                    <div className="text-xs text-muted-foreground italic mt-0.5">{s.notes}</div>
                  )}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5"
                  onClick={() => downloadManagerSettlement(s.id)}
                  data-testid={`mgr-ledger-settlement-pdf-${s.id}`}
                >
                  <Printer className="h-3.5 w-3.5" /> Receipt
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <AddEntryDialog open={addOpen} onOpenChange={setAddOpen} onCreated={() => load()} />
      <AddEntryDialog open={addExpenseOpen} onOpenChange={setAddExpenseOpen} onCreated={() => load()} mode="expense" />
      <SettleDialog
        open={settleOpen}
        onOpenChange={setSettleOpen}
        totalHeld={totalHeld}
        onSettled={() => load()}
      />
      <LedgerFullHistoryDialog
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        variant="manager"
        ledgerLabel="My cash ledger"
      />
    </div>
  );
}

function SummaryTile({ icon, label, value, sub, tone, highlight, testid }) {
  // tone: "amber" (default highlight), "rose" (expense), "negative" (held<0).
  const t = tone || (highlight ? "amber" : null);
  const cls =
    t === "negative" ? "bg-rose-50 border-rose-300 ring-1 ring-rose-200"
      : t === "rose" ? "bg-rose-50 border-rose-200"
      : t === "amber" ? "bg-amber-50 border-amber-200"
      : "bg-card border-border";
  const valueCls =
    t === "negative" ? "text-rose-900"
      : t === "amber" ? "text-amber-900"
      : "";
  return (
    <div className={`rounded-[8px] border p-4 ${cls}`} data-testid={testid}>
      <div className="flex items-center gap-2">
        {icon}
        <div className="mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
          {label}
        </div>
      </div>
      <div className={`text-2xl font-black mt-2 ${valueCls}`}>{value}</div>
      <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>
    </div>
  );
}

// Phase P.14 — bank-statement-style entries table for the Manager Ledger.
// Columns: Date | Description | Credit | Debit | Balance. Running balance
// is computed client-side by walking entries oldest→newest (intake adds,
// expense subtracts). Mobile: table collapses to stacked card rows via
// overflow-x-auto + a narrower breakpoint variant.
function LedgerStatementTable({ entries, onChanged }) {
  // Sort ascending by entry_date to build running balance, keep the map by
  // id, then render the original order (newest first as backend delivered).
  const balanceById = useMemo(() => {
    const asc = [...entries].sort((a, b) =>
      String(a.entry_date || "").localeCompare(String(b.entry_date || ""))
    );
    const map = new Map();
    let running = 0;
    for (const e of asc) {
      const amt = Number(e.amount || 0);
      const sign = (e.kind || "intake") === "expense" ? -1 : 1;
      running += sign * amt;
      map.set(e.id, running);
    }
    return map;
  }, [entries]);

  const totals = useMemo(() => {
    let credits = 0; let debits = 0;
    for (const e of entries) {
      const amt = Number(e.amount || 0);
      if ((e.kind || "intake") === "expense") debits += amt;
      else credits += amt;
    }
    return { credits, debits, balance: credits - debits };
  }, [entries]);

  return (
    <div data-testid="mgr-ledger-entries-list">
      {/* Header totals band */}
      <div className="bg-secondary/40 border border-border rounded-t-[8px] px-4 py-2 flex flex-wrap items-center gap-x-6 gap-y-1 text-xs mono"
           data-testid="mgr-ledger-statement-totals">
        <div>
          <span className="uppercase tracking-widest text-muted-foreground">Credits</span>{" "}
          <span className="font-black text-emerald-700">+{fmtMoney(totals.credits)}</span>
        </div>
        <div>
          <span className="uppercase tracking-widest text-muted-foreground">Debits</span>{" "}
          <span className="font-black text-rose-700">−{fmtMoney(totals.debits)}</span>
        </div>
        <div>
          <span className="uppercase tracking-widest text-muted-foreground">Balance</span>{" "}
          <span className={`font-black tabular-nums ${totals.balance < 0 ? "text-rose-800" : "text-foreground"}`}>{fmtMoney(totals.balance)}</span>
        </div>
      </div>

      {/* Desktop table */}
      <div className="hidden md:block bg-card border border-border border-t-0 rounded-b-[8px] overflow-x-auto">
        <table className="w-full text-sm" data-testid="mgr-ledger-statement-table">
          <thead className="bg-muted/40 text-muted-foreground">
            <tr>
              <th className="text-left px-3 py-2 font-bold mono text-[10px] uppercase tracking-widest">Date</th>
              <th className="text-left px-3 py-2 font-bold mono text-[10px] uppercase tracking-widest">Description</th>
              <th className="text-right px-3 py-2 font-bold mono text-[10px] uppercase tracking-widest w-28">Credit</th>
              <th className="text-right px-3 py-2 font-bold mono text-[10px] uppercase tracking-widest w-28">Debit</th>
              <th className="text-right px-3 py-2 font-bold mono text-[10px] uppercase tracking-widest w-32">Balance</th>
              <th className="text-right px-3 py-2 w-10" />
            </tr>
          </thead>
          <tbody>
            {entries.map((e) => (
              <StatementRow
                key={e.id}
                entry={e}
                runningBalance={balanceById.get(e.id) ?? 0}
                onChanged={onChanged}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile: single-column card rows */}
      <div className="md:hidden bg-card border border-border border-t-0 rounded-b-[8px] divide-y divide-border">
        {entries.map((e) => (
          <StatementRowMobile
            key={e.id}
            entry={e}
            runningBalance={balanceById.get(e.id) ?? 0}
            onChanged={onChanged}
          />
        ))}
      </div>
    </div>
  );
}

function StatementRow({ entry, runningBalance, onChanged }) {
  const isSettled = !!entry.settlement_id;
  const isExpense = (entry.kind || "intake") === "expense";
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const bookingId = entry.source_booking_id || null;
  const amt = Number(entry.amount || 0);
  const desc = buildEntryDescription(entry);
  return (
    <tr className="border-t border-border hover:bg-secondary/30" data-testid={`mgr-ledger-entry-${entry.id}`}>
      <td className="px-3 py-2 mono text-xs text-muted-foreground whitespace-nowrap align-top">
        {fmtDateTime(entry.entry_date)}
      </td>
      <td className="px-3 py-2 align-top">
        <div className="text-sm font-bold">{desc.title}</div>
        {desc.sub && <div className="text-xs text-muted-foreground mt-0.5">{desc.sub}</div>}
        <div className="flex flex-wrap items-center gap-1.5 mt-1">
          {entry.source === "booking" && (
            <span className="mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">Auto</span>
          )}
          {entry.source === "staff_settlement" && (
            <span className="mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-sky-50 text-sky-700 border border-sky-200">From Settlement</span>
          )}
          {entry.source === "customer_direct" && (
            <span className="mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-200">From Customer</span>
          )}
          {isSettled && (
            <span className="mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-muted text-muted-foreground border border-border">Settled</span>
          )}
        </div>
      </td>
      <td className="px-3 py-2 text-right align-top mono tabular-nums font-bold whitespace-nowrap">
        {!isExpense ? <span className="text-emerald-700">+{fmtMoney(amt, entry.currency)}</span> : <span className="text-muted-foreground">—</span>}
      </td>
      <td className="px-3 py-2 text-right align-top mono tabular-nums font-bold whitespace-nowrap">
        {isExpense ? <span className="text-rose-700">−{fmtMoney(amt, entry.currency)}</span> : <span className="text-muted-foreground">—</span>}
      </td>
      <td className={`px-3 py-2 text-right align-top mono tabular-nums font-black whitespace-nowrap ${runningBalance < 0 ? "text-rose-800" : "text-foreground"}`}>
        {fmtMoney(runningBalance, entry.currency)}
      </td>
      <td className="px-2 py-2 text-right align-top">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="p-1.5 rounded-md hover:bg-secondary text-muted-foreground hover:text-foreground"
              data-testid={`mgr-ledger-entry-${entry.id}-actions`}
              aria-label="Ledger entry actions"
            >
              <MoreHorizontal className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-40">
            {!isSettled && (
              <>
                <DropdownMenuItem
                  onClick={() => setEditOpen(true)}
                  data-testid={`mgr-ledger-entry-${entry.id}-edit`}
                >
                  <Pencil className="h-3.5 w-3.5 mr-2" /> Edit
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setDeleteOpen(true)}
                  className="text-rose-700 focus:text-rose-800 focus:bg-rose-50"
                  data-testid={`mgr-ledger-entry-${entry.id}-delete`}
                >
                  <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}
            <DropdownMenuItem
              onClick={() => setHistoryOpen(true)}
              data-testid={`mgr-ledger-entry-${entry.id}-history`}
            >
              <HistoryIcon className="h-3.5 w-3.5 mr-2" /> History
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <LedgerEditDialog
          entry={entry}
          open={editOpen}
          onOpenChange={setEditOpen}
          onSaved={() => { setEditOpen(false); onChanged?.(); }}
        />
        <LedgerDeleteDialog
          entry={entry}
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
          onDeleted={() => { setDeleteOpen(false); onChanged?.(); }}
          bookingId={bookingId}
        />
        <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
          <DialogContent data-testid={`mgr-ledger-entry-${entry.id}-history-dialog`}>
            <DialogHeader>
              <DialogTitle>Entry history</DialogTitle>
              <DialogDescription>
                Audit trail for {fmtMoney(entry.amount, entry.currency)} · {fmtDateTime(entry.entry_date)}
              </DialogDescription>
            </DialogHeader>
            <StatusHistory
              entityType="cash_ledger_entry"
              entityId={entry.id}
              defaultOpen
              testIdPrefix={`mgr-ledger-entry-${entry.id}-history-timeline`}
            />
          </DialogContent>
        </Dialog>
      </td>
    </tr>
  );
}

function StatementRowMobile({ entry, runningBalance, onChanged }) {
  const isExpense = (entry.kind || "intake") === "expense";
  const desc = buildEntryDescription(entry);
  const amt = Number(entry.amount || 0);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const bookingId = entry.source_booking_id || null;
  const isSettled = !!entry.settlement_id;
  return (
    <div className="px-3 py-3 flex items-start justify-between gap-3" data-testid={`mgr-ledger-entry-${entry.id}-mobile`}>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-bold truncate">{desc.title}</div>
        {desc.sub && <div className="text-xs text-muted-foreground truncate">{desc.sub}</div>}
        <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground mt-1">
          {fmtDateTime(entry.entry_date)}
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className={`mono tabular-nums font-black ${isExpense ? "text-rose-700" : "text-emerald-700"}`}>
          {isExpense ? "−" : "+"}{fmtMoney(amt, entry.currency)}
        </div>
        <div className={`mono text-[10px] mt-0.5 ${runningBalance < 0 ? "text-rose-800" : "text-muted-foreground"}`}>
          bal {fmtMoney(runningBalance, entry.currency)}
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="p-1 mt-1 text-muted-foreground" data-testid={`mgr-ledger-entry-${entry.id}-actions-mobile`}>
              <MoreHorizontal className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-40">
            {!isSettled && (
              <>
                <DropdownMenuItem onClick={() => setEditOpen(true)}><Pencil className="h-3.5 w-3.5 mr-2" />Edit</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setDeleteOpen(true)} className="text-rose-700"><Trash2 className="h-3.5 w-3.5 mr-2" />Delete</DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}
            <DropdownMenuItem onClick={() => setHistoryOpen(true)}><HistoryIcon className="h-3.5 w-3.5 mr-2" />History</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <LedgerEditDialog entry={entry} open={editOpen} onOpenChange={setEditOpen} onSaved={() => { setEditOpen(false); onChanged?.(); }} />
      <LedgerDeleteDialog entry={entry} open={deleteOpen} onOpenChange={setDeleteOpen} onDeleted={() => { setDeleteOpen(false); onChanged?.(); }} bookingId={bookingId} />
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Entry history</DialogTitle>
            <DialogDescription>{fmtMoney(entry.amount, entry.currency)} · {fmtDateTime(entry.entry_date)}</DialogDescription>
          </DialogHeader>
          <StatusHistory entityType="cash_ledger_entry" entityId={entry.id} defaultOpen />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function buildEntryDescription(entry) {
  const isExpense = (entry.kind || "intake") === "expense";
  const isSimple = entry.cash_type === "simple";
  const kindLabel = isExpense
    ? (entry.category ? `Expense · ${entry.category}` : "Expense")
    : (isSimple ? "Cash intake (off-books)" : "Cash intake (invoiced)");
  const bits = [];
  if (entry.customer_name) bits.push(entry.customer_name);
  if (entry.source_booking_id) {
    const code = entry.booking_code || (entry.source_booking_id || "").slice(0, 8);
    bits.push(`Booking ${code}`);
  }
  if (entry.linked_invoice_number) bits.push(entry.linked_invoice_number);
  if (entry.notes) bits.push(entry.notes);
  return { title: kindLabel, sub: bits.join(" · ") };
}

function EntryRow_legacy_unused({ entry, onChanged }) {
  const isSettled = !!entry.settlement_id;
  const isSimple = entry.cash_type === "simple";
  const isExpense = (entry.kind || "intake") === "expense";
  // Phase P.13 — action menu state.
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  // Job link surfacing (audit dialog copy + soft warning on delete).
  const bookingId = entry.source_booking_id || null;
  return (
    <div
      className={`bg-card border rounded-[8px] px-4 py-3 flex items-center justify-between gap-3 flex-wrap ${
        isExpense ? "border-rose-200 hover:border-rose-300" : "border-border"
      }`}
      data-testid={`mgr-ledger-entry-${entry.id}`}
    >
      <div className="flex items-start gap-3 min-w-0 flex-1">
        <div className={`mt-1 rounded-full p-1.5 ${
          isExpense
            ? "bg-rose-100 text-rose-700"
            : isSimple ? "bg-violet-100 text-violet-700" : "bg-sky-100 text-sky-700"
        }`}>
          {isExpense ? <Minus className="h-4 w-4" /> : (isSimple ? <Coins className="h-4 w-4" /> : <FileText className="h-4 w-4" />)}
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-base font-bold ${isExpense ? "text-rose-700" : ""}`}>
              {isExpense ? "−" : ""}{fmtMoney(entry.amount, entry.currency)}
            </span>
            {isExpense && entry.category && (
              <span className="mono text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200">
                {entry.category}
              </span>
            )}
            <span className={`mono text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${
              isSimple ? "bg-violet-50 text-violet-700 border-violet-200"
                       : "bg-sky-50 text-sky-700 border-sky-200"
            }`}>
              {isSimple ? "Simple" : "Invoice"}
            </span>
            {entry.source === "booking" && (
              <span className="mono text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                Auto · Booking
              </span>
            )}
            {isSettled && (
              <span className="mono text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-muted text-muted-foreground border border-border">
                Settled
              </span>
            )}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">
            {fmtDateTime(entry.entry_date)}
            {entry.customer_name ? ` · ${entry.customer_name}` : ""}
            {entry.source_booking_id ? ` · Booking ${entry.source_booking_id.slice(0, 8)}` : ""}
          </div>
          {entry.notes && (
            <div className="text-xs text-muted-foreground italic mt-1">{entry.notes}</div>
          )}
        </div>
      </div>
      {/* Phase P.13 — actions kebab. Settled entries only expose History. */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="p-1.5 rounded-md hover:bg-secondary text-muted-foreground hover:text-foreground shrink-0"
            data-testid={`mgr-ledger-entry-${entry.id}-actions`}
            aria-label="Ledger entry actions"
          >
            <MoreHorizontal className="h-4 w-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-40">
          {!isSettled && (
            <>
              <DropdownMenuItem
                onClick={() => setEditOpen(true)}
                data-testid={`mgr-ledger-entry-${entry.id}-edit`}
              >
                <Pencil className="h-3.5 w-3.5 mr-2" /> Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setDeleteOpen(true)}
                className="text-rose-700 focus:text-rose-800 focus:bg-rose-50"
                data-testid={`mgr-ledger-entry-${entry.id}-delete`}
              >
                <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}
          <DropdownMenuItem
            onClick={() => setHistoryOpen(true)}
            data-testid={`mgr-ledger-entry-${entry.id}-history`}
          >
            <HistoryIcon className="h-3.5 w-3.5 mr-2" /> History
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <LedgerEditDialog
        entry={entry}
        open={editOpen}
        onOpenChange={setEditOpen}
        onSaved={() => { setEditOpen(false); onChanged?.(); }}
      />
      <LedgerDeleteDialog
        entry={entry}
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        onDeleted={() => { setDeleteOpen(false); onChanged?.(); }}
        bookingId={bookingId}
      />
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent data-testid={`mgr-ledger-entry-${entry.id}-history-dialog`}>
          <DialogHeader>
            <DialogTitle>Entry history</DialogTitle>
            <DialogDescription>
              Audit trail for {fmtMoney(entry.amount, entry.currency)} · {fmtDateTime(entry.entry_date)}
            </DialogDescription>
          </DialogHeader>
          <StatusHistory
            entityType="cash_ledger_entry"
            entityId={entry.id}
            defaultOpen
            testIdPrefix={`mgr-ledger-entry-${entry.id}-history-timeline`}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}


// Phase P.13 — Edit dialog for a manager ledger entry.
function LedgerEditDialog({ entry, open, onOpenChange, onSaved }) {
  const [amount, setAmount] = useState(String(entry.amount ?? ""));
  const [note, setNote] = useState(entry.notes || "");
  const [entryDate, setEntryDate] = useState(
    (entry.entry_date || "").slice(0, 16),
  );
  const [kind, setKind] = useState(entry.kind || "intake");
  const [cashType, setCashType] = useState(entry.cash_type || "invoice");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) {
      setAmount(String(entry.amount ?? ""));
      setNote(entry.notes || "");
      setEntryDate((entry.entry_date || "").slice(0, 16));
      setKind(entry.kind || "intake");
      setCashType(entry.cash_type || "invoice");
    }
  }, [open, entry]);
  const save = async () => {
    setSaving(true);
    try {
      const body = {
        amount: Number(amount),
        note,
        entry_date: entryDate ? new Date(entryDate).toISOString() : undefined,
        kind,
        cash_type: cashType,
      };
      await api.patch(`/manager-ledger/entries/${entry.id}`, body);
      toast.success("Entry updated");
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={`mgr-ledger-edit-${entry.id}`}>
        <DialogHeader>
          <DialogTitle>Edit ledger entry</DialogTitle>
          <DialogDescription>
            Adjust amount, note, or date. Changes are recorded in the entry&apos;s audit history.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Amount ({entry.currency || "EUR"})</Label>
            <Input
              type="number" step="0.01" min="0.01" value={amount}
              onChange={(e) => setAmount(e.target.value)}
              data-testid={`mgr-ledger-edit-${entry.id}-amount`}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Direction</Label>
              <select
                className="h-10 w-full border border-border rounded-md bg-background px-2 text-sm"
                value={kind}
                onChange={(e) => setKind(e.target.value)}
                data-testid={`mgr-ledger-edit-${entry.id}-kind`}
              >
                <option value="intake">In (intake)</option>
                <option value="expense">Out (expense)</option>
              </select>
            </div>
            <div>
              <Label>Cash type</Label>
              <select
                className="h-10 w-full border border-border rounded-md bg-background px-2 text-sm"
                value={cashType}
                onChange={(e) => setCashType(e.target.value)}
                data-testid={`mgr-ledger-edit-${entry.id}-cash-type`}
              >
                <option value="invoice">Invoice (in-books)</option>
                <option value="simple">Simple (off-books)</option>
              </select>
            </div>
          </div>
          <div>
            <Label>Occurred at</Label>
            <Input
              type="datetime-local"
              value={entryDate}
              onChange={(e) => setEntryDate(e.target.value)}
              data-testid={`mgr-ledger-edit-${entry.id}-date`}
            />
          </div>
          <div>
            <Label>Note</Label>
            <Textarea
              value={note} onChange={(e) => setNote(e.target.value)}
              data-testid={`mgr-ledger-edit-${entry.id}-note`} rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={save} disabled={saving} data-testid={`mgr-ledger-edit-${entry.id}-save`}>
            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function LedgerDeleteDialog({ entry, open, onOpenChange, onDeleted, bookingId }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      await api.delete(`/manager-ledger/entries/${entry.id}`);
      toast.success("Entry deleted");
      onDeleted?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={`mgr-ledger-delete-${entry.id}`}>
        <DialogHeader>
          <DialogTitle>Delete this ledger entry?</DialogTitle>
          <DialogDescription>This cannot be undone.</DialogDescription>
        </DialogHeader>
        {bookingId && (
          <div
            className="text-sm bg-amber-50 border border-amber-200 text-amber-900 rounded-md p-3 flex items-start gap-2"
            data-testid={`mgr-ledger-delete-${entry.id}-job-warning`}
          >
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              This entry is linked to job{" "}
              <span className="mono font-bold">{entry.booking_code || bookingId.slice(0, 8)}</span>.
              Deleting will not affect the job itself, but the audit trail will keep a record.
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button
            onClick={run}
            disabled={busy}
            className="bg-rose-600 hover:bg-rose-700 text-white"
            data-testid={`mgr-ledger-delete-${entry.id}-confirm`}
          >
            {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1" />}
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
